import React, { useState, useEffect } from "react";
import moment from "moment";
import PropTypes from "prop-types";
import axios from "axios";
import ShowReceipt from "./ShowReceipt";
import Table from "../../core/pagination/datatable"; // Import the Table component

const DateRangeSelector = ({ onDateChange, startDate, endDate }) => {
  const [state, setState] = useState({
    start: startDate ? moment(startDate) : moment().startOf('day'),
    end: endDate ? moment(endDate) : moment().endOf('day'),
  });

  // Keep in sync when parent updates filters
  useEffect(() => {
    if (startDate && endDate) {
      setState({
        start: moment(startDate),
        end: moment(endDate)
      });
    }
  }, [startDate, endDate]);

  const { start, end } = state;

  const handleStartDateChange = (e) => {
    const newStart = moment(e.target.value);
    setState(prev => ({ ...prev, start: newStart }));
  };

  const handleEndDateChange = (e) => {
    const newEnd = moment(e.target.value);
    setState(prev => ({ ...prev, end: newEnd }));
  };

  const handleApply = () => {
    if (onDateChange) {
      // Convert to UTC dates at start and end of day
      onDateChange({
        startDate: start.startOf('day').toDate(),
        endDate: end.endOf('day').toDate()
      });
    }
  };

  const isApplyDisabled = !start.isValid() || !end.isValid() || start.isAfter(end);

  return (
    <div className="d-flex align-items-end gap-4 flex-wrap">
      <div className="d-flex align-items-end gap-4 flex-grow-1">
        <div className="flex-grow-1">
          <label className="form-label mb-1 small fw-semibold">From Date:</label>
          <input
            type="date"
            className="form-control form-control-sm"
            value={start.format("YYYY-MM-DD")}
            onChange={handleStartDateChange}
            onClick={(e) => e.target.showPicker()} // This ensures calendar opens on click
            style={{ minWidth: "140px", cursor: "pointer" }}
          />
        </div>
        <div className="flex-grow-1">
          <label className="form-label mb-1 small fw-semibold">To Date:</label>
          <input
            type="date"
            className="form-control form-control-sm"
            value={end.format("YYYY-MM-DD")}
            onChange={handleEndDateChange}
            onClick={(e) => e.target.showPicker()} // This ensures calendar opens on click
            style={{ minWidth: "140px", cursor: "pointer" }}
          />
        </div>

        {/* Apply Button moved inside the same flex container */}
        <div className="flex-grow-0">
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={handleApply}
            disabled={isApplyDisabled}
            style={{ height: "32px", minWidth: "80px" }}
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

// Add PropTypes validation
DateRangeSelector.propTypes = {
  onDateChange: PropTypes.func,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
};

const NewDashboard = () => {
  const [salesData, setSalesData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    dateFrom: moment().startOf('day').toISOString(),
    dateTo: moment().toISOString(),
    categoryId: null,
    groupBy: "day",
    paymentMethod: "",
  });
  const [showPaymentClearButton, setShowPaymentClearButton] = useState(false);
  const [showClearButton, setShowClearButton] = useState(false);
  const [datePickerKey, setDatePickerKey] = useState(0);
  const [modalState, setModalState] = useState({
    show: false,
    message: ""
  });
  const [receiptModal, setReceiptModal] = useState({
    show: false,
    token: null,
    receiptData: null,
    loading: false,
    error: null
  });

  // Add delete modal state
  const [deleteModal, setDeleteModal] = useState({
    show: false,
    token: null,
    loading: false
  });

  // Pagination and search states
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filteredTokens, setFilteredTokens] = useState([]);

  const apiBase =
    process.env.REACT_APP_BASEURL ||
    window.__APP_CONFIG__?.API_BASE_URL || "";

  useEffect(() => {
    fetchSalesReport();
  }, [filters]);

  // Filter tokens based on search term
  useEffect(() => {
    if (salesData && salesData.tokens) {
      let filtered = [...salesData.tokens];

      if (searchTerm.trim()) {
        const query = searchTerm.toLowerCase().trim();
        filtered = filtered.filter(token =>
          (token.tokenNumber && token.tokenNumber.toString().toLowerCase().includes(query)) ||
          (token.totalPrice && token.totalPrice.toString().toLowerCase().includes(query)) ||
          (token.latestCreatedAt && new Date(token.latestCreatedAt).toLocaleDateString("en-CA").toLowerCase().includes(query))
        );
      }

      setFilteredTokens(filtered);
      setCurrentPage(1); // Reset to first page when search changes
    }
  }, [salesData, searchTerm]);

  const fetchSalesReport = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();

      if (filters.dateFrom) params.append("dateFrom", filters.dateFrom);
      if (filters.dateTo) params.append("dateTo", filters.dateTo);
      if (filters.categoryId !== null && filters.categoryId !== undefined) {
        params.append("categoryId", filters.categoryId);
      }
      if (filters.groupBy) params.append("groupBy", filters.groupBy);
      if (filters.paymentMethod) params.append("paymentMethod", filters.paymentMethod);

      const response = await axios.get(
        `${apiBase}api/v1/Order/SalesReport?${params.toString()}`
      );

      setSalesData(response.data);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching sales report:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = (dates) => {
    setFilters({
      ...filters,
      dateFrom: moment(dates.startDate).startOf('day').toISOString(),
      dateTo: moment(dates.endDate).endOf('day').toDate().toISOString(),
    });

    setShowClearButton(true);
  };

  const clearDateFilter = () => {
    const defaultDateFrom = moment().startOf('day').toISOString();
    const defaultDateTo = moment().toISOString();

    setFilters({
      ...filters,
      dateFrom: defaultDateFrom,
      dateTo: defaultDateTo,
    });

    setShowClearButton(false);
    setDatePickerKey(prev => prev + 1);
  };

  const handleViewReceipt = async (token) => {
    try {
      setReceiptModal({
        show: true,
        token: token,
        receiptData: null,
        loading: true,
        error: null
      });

      const orderDate = moment(token.latestCreatedAt).format("YYYY-MM-DD");

      const response = await axios.get(
        `${apiBase}api/v1/Order/ReceiptByTokenV2`,
        {
          params: {
            tokenNumber: token.tokenNumber,
            date: orderDate
          }
        }
      );

      setReceiptModal(prev => ({
        ...prev,
        receiptData: response.data,
        loading: false
      }));
    } catch (err) {
      console.error("Error fetching receipt:", err);
      setReceiptModal(prev => ({
        ...prev,
        error: err.response?.data?.message || "Failed to load receipt",
        loading: false
      }));
    }
  };

  const handleCloseReceipt = () => {
    setReceiptModal({
      show: false,
      token: null,
      receiptData: null,
      loading: false,
      error: null
    });
  };

  // Add delete order functions
  const handleCancelOrder = (token) => {
    setDeleteModal({
      show: true,
      token: token,
      loading: false
    });
  };

  const handleConfirmDelete = async () => {
    if (!deleteModal.token) return;

    try {
      setDeleteModal(prev => ({ ...prev, loading: true }));

      const orderDate = moment(deleteModal.token.latestCreatedAt).format("YYYY-MM-DD");

      await axios.delete(
        `${apiBase}api/v1/Order/CancelOrder`,
        {
          params: {
            tokenNumber: deleteModal.token.tokenNumber,
            date: orderDate,
            orderId: deleteModal.token.orderId
          }
        }
      );

      // Show success message
      setModalState({
        show: true,
        message: "Order deleted successfully!"
      });

      // Refresh the sales report
      await fetchSalesReport();

    } catch (err) {
      console.error("Error deleting order:", err);
      setModalState({
        show: true,
        message: err.response?.data?.message || "Failed to delete order"
      });
    } finally {
      // Close delete modal regardless of success or failure
      setDeleteModal({
        show: false,
        token: null,
        loading: false
      });
    }
  };

  const handleCloseDeleteModal = () => {
    setDeleteModal({
      show: false,
      token: null,
      loading: false
    });
  };

  const handlePaymentMethodChange = (e) => {
    const selectedMethod = e.target.value;
    setFilters({
      ...filters,
      paymentMethod: selectedMethod
    });
    setShowPaymentClearButton(selectedMethod !== "");
  };

  const clearPaymentMethodFilter = () => {
    setFilters({
      ...filters,
      paymentMethod: ""
    });
    setShowPaymentClearButton(false);
  };

  // Calculate category sales
  const calculateCategorySales = () => {
    if (!salesData || !salesData.summary) {
      return { totalSales: 0, fastFoodSales: 0, mainSales: 0 };
    }

    const totalSales = salesData.summary.totalSales || 0;
    let fastFoodSales = 0;
    let mainSales = 0;

    if (salesData.summary.categorySummary) {
      salesData.summary.categorySummary.forEach(category => {
        if (category.categoryName === "Fast Food") {
          fastFoodSales += category.totalSalesAmount;
        } else if (category.categoryName === "Main") {
          mainSales += category.totalSalesAmount;
        }
      });
    }

    return { totalSales, fastFoodSales, mainSales };
  };

  // Define table columns
  const columns = [
    {
      title: "Date",
      dataIndex: "latestCreatedAt",
      flex: 1,
      width: '20%',
      render: (text) => (
        <div>
          {new Date(text).toLocaleDateString("en-CA")}
        </div>
      ),
      sorter: (a, b) => new Date(a.latestCreatedAt) - new Date(b.latestCreatedAt),
    },
    {
      title: "Token",
      dataIndex: "tokenNumber",
      flex: 1,
      width: '20%',
      render: (text) => (
        <div>
          <strong> {text}</strong>
        </div>
      ),
      sorter: (a, b) => a.tokenNumber - b.tokenNumber,
    },
    {
      title: "Amount",
      dataIndex: "totalPrice",
      flex: 1,
      width: '20%',
      render: (text) => `₹${text.toFixed(2)}`,
      sorter: (a, b) => a.totalPrice - b.totalPrice,
    },
    {
      title: "Actions",
      dataIndex: "action",
      flex: 1,
      width: '20%',
      render: (_, record) => (
        <div className="d-flex gap-2">
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => handleViewReceipt(record)}
          >
            View Receipt
          </button>
          <button
            className="btn btn-sm btn-outline-danger"
            onClick={() => handleCancelOrder(record)}
          >
            Cancel
          </button>
        </div>
      ),
    }
  ];

  // Empty text component for the table
  const emptyText = (
    <div className="text-center py-4">
      <i className="ti ti-file-text fs-24 text-muted mb-2"></i>
      <h5>No tokens found</h5>
      <p className="text-muted">
        {salesData && salesData.tokens && salesData.tokens.length > 0
          ? "No tokens match your search criteria"
          : "No tokens found for the selected date range"}
      </p>
    </div>
  );

  const { totalSales, fastFoodSales, mainSales } = calculateCategorySales();

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: "300px" }}
          >
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="alert alert-danger">
            Error loading sales data: {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Header with reduced margin */}
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-1">
          <div>
            <h3 className="mb-0">Sales Reports</h3>
          </div>
        </div>

        {/* Date Range Selector with reduced margin */}
        <div className="row mb-2">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-body py-3">
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                  {/* Left side - Date Range Selector */}
                  <div className="d-flex align-items-end gap-4 flex-grow-1 flex-wrap">
                    <DateRangeSelector
                      key={datePickerKey}
                      onDateChange={handleDateRangeChange}
                      startDate={filters.dateFrom}
                      endDate={filters.dateTo}
                    />

                    {showClearButton && (
                      <div className="flex-grow-0">
                        <button
                          className="btn btn-outline-secondary btn-sm"
                          onClick={clearDateFilter}
                          style={{ height: '32px', minWidth: '80px' }}
                          title="Clear date filter"
                        >
                          <i className="ti ti-refresh me-1"></i>
                          Refresh
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Right side - Payment Method Filter */}
                  <div className="d-flex align-items-end gap-2 flex-grow-0 flex-wrap">
                    <div className="flex-grow-0">
                      <label className="form-label mb-1 small fw-semibold">Filter:</label>
                      <select
                        className="form-select form-select-sm"
                        value={filters.paymentMethod}
                        onChange={handlePaymentMethodChange}
                        style={{ minWidth: "140px", height: "32px" }}
                      >
                        <option value="">Select</option>
                        <option value="Online">Online</option>
                        <option value="Cash">Cash</option>
                      </select>
                    </div>

                    {showPaymentClearButton && (
                      <div className="flex-grow-0">
                        <button
                          className="btn btn-outline-secondary btn-sm"
                          onClick={clearPaymentMethodFilter}
                          style={{ height: '32px', minWidth: '80px' }}
                          title="Clear payment filter"
                        >
                          <i className="ti ti-refresh me-1"></i>
                          Clear
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Cards with reduced margin */}
        <div className="row mb-1">
          {/* Total Sales Card */}
          <div className="col-xl-4 col-sm-6 col-12 d-flex mb-3">
            <div className="card bg-primary sale-widget flex-fill">
              <div className="card-body d-flex align-items-center">
                <span className="sale-icon bg-white text-primary">
                  <i className="ti ti-file-text fs-24" />
                </span>
                <div className="ms-2 ">
                  <p className="text-white mb-1 ">Total Sales</p>
                  <div className="d-inline-flex align-items-center flex-wrap gap-2">
                    <h4 className="text-white">₹{totalSales.toFixed(2)}</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Fast Food Sales Card */}
          <div className="col-xl-4 col-sm-6 col-12 d-flex mb-3">
            <div className="card bg-teal sale-widget flex-fill">
              <div className="card-body d-flex align-items-center">
                <span className="sale-icon bg-white text-teal">
                  <i className="ti ti-shopping-cart fs-24" />
                </span>
                <div className="ms-2">
                  <p className="text-white mb-1">Fast Food Sales</p>
                  <div className="d-inline-flex align-items-center flex-wrap gap-2">
                    <h4 className="text-white">₹{fastFoodSales.toFixed(2)}</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Sales Card */}
          <div className="col-xl-4 col-sm-6 col-12 d-flex mb-3">
            <div className="card bg-orange sale-widget flex-fill">
              <div className="card-body d-flex align-items-center">
                <span className="sale-icon bg-white text-orange">
                  <i className="ti ti-receipt fs-24" />
                </span>
                <div className="ms-2">
                  <p className="text-white mb-1">Main Sales</p>
                  <div className="d-inline-flex align-items-center flex-wrap gap-2">
                    <h4 className="text-white">₹{mainSales.toFixed(2)}</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sales Report Table with Token data */}
        <div className="row mb-1">
          <div className="col-12 d-flex">
            <div className="card flex-fill">
              <div className="card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
                <h5 className="card-title mb-0">Sales Details</h5>

                {/* Search Input */}
                <div className="search-input position-relative" style={{ minWidth: '200px', maxWidth: '200px', flex: '1' }}>
                  <div className="input-group">
                    <span className="input-group-text bg-white border-end-0">
                      <i className="ti ti-search text-muted"></i>
                    </span>
                    <input
                      type="search"
                      className="form-control form-control-sm border-start-0"
                      placeholder="Search tokens..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <Table
                    columns={columns}
                    dataSource={filteredTokens}
                    loading={loading}
                    pagination={{
                      PageSize: pageSize,
                      current: currentPage,
                      onChange: (page, size) => {
                        setCurrentPage(page);
                        setPageSize(size);
                      }
                    }}
                    scroll={{ x: 'max-content' }}
                    emptyText={emptyText}
                    hideSearch={true}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal for showing messages */}
      {modalState.show && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Alert</h5>
              </div>
              <div className="modal-body">
                <p>{modalState.message}</p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setModalState({ show: false, message: "" })}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal.show && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Delete</h5>
              </div>
              <div className="modal-body">
                <p>
                  Are you sure you want to delete order with token number{" "}
                  <strong>{deleteModal.token?.tokenNumber}</strong>?
                  <div>This action cannot be undone.</div>
                </p>
              </div>
              <div className="modal-footer gap-2">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCloseDeleteModal}
                  disabled={deleteModal.loading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleConfirmDelete}
                  disabled={deleteModal.loading}
                >
                  {deleteModal.loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Deleting...
                    </>
                  ) : (
                    'Delete'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {receiptModal.show && (
        <ShowReceipt
          token={receiptModal.token}
          receiptData={receiptModal.receiptData}
          loading={receiptModal.loading}
          error={receiptModal.error}
          onClose={handleCloseReceipt}
        />
      )}

      <div className="copyright-footer d-flex align-items-center justify-content-between border-top bg-white gap-3 flex-wrap">
        <p className="fs-13 text-gray-9 mb-0">
          2025 © Belinnov Solutions. All Right Reserved
        </p>
      </div>

      <style>
        {`
          .scrollbar-hidden::-webkit-scrollbar {
            display: none;
          }
          .scrollbar-hidden {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          .form-control-sm {
            padding: 0.375rem 0.75rem;
            font-size: 0.875rem;
          }
          .btn-sm {
            padding: 0.375rem 0.75rem;
            font-size: 0.875rem;
          }
          .input-group-text {
            font-size: 0.875rem;
            padding: 0.375rem 0.75rem;
          }
          .border-start-0 {
            border-left: 0 !important;
          }
          .border-end-0 {
            border-right: 0 !important;
          }
        `}
      </style>
    </div>
  );
};

export default NewDashboard;